import { Component, OnInit, OnDestroy, AfterViewInit, signal, Signal, computed, WritableSignal } from '@angular/core';

import Feature from 'ol/Feature';
import { Capacitor, PermissionState } from '@capacitor/core';
import { Geolocation as CapacitorGeolocation, PermissionStatus, Position } from '@capacitor/geolocation';
import { Vector as VectorLayer } from 'ol/layer';
import { Vector as VectorSource } from 'ol/source';
import { unByKey } from 'ol/Observable';
import { Circle as CircleStyle, Fill, RegularShape, Stroke, Style } from 'ol/style';
import { MatSnackBar } from '@angular/material/snack-bar';
import * as olProj from 'ol/proj';

import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MapService } from '../../services/map.service';
import { Subscription } from 'rxjs';
import { ViewService } from '../../services/view.service';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { Coordinate } from 'ol/coordinate';
import { EventsKey } from 'ol/events';
import { ToggleEventNameType, ToggleEventService, ToggleEventType } from '@tt-lib/services/toggle-event.service';
import { LineString, Point, Circle } from 'ol/geom';
import { GeolocationService } from '@tt-lib/services/geolocation.service';
import BaseEvent from 'ol/events/Event';
import { BrowserSnifferService } from '@tt-lib/services/browser-sniffer.service';
import { OlLayerService } from '@tt-lib/services/ol/ol-layer.service';
import { Coord } from '@turf/helpers';

export enum GeolocState { OFF, FOCUSED, UNFOCUSED };
export enum GeolocStateUpdate { INIT, AUTO_DOING, DOING, DONE };

@Component({
  selector: 'tt-geolocation',
  templateUrl: './geolocation.component.html',
  styleUrls: ['./geolocation.component.scss'],
  imports: [
    CommonModule,
    TranslateModule,
    MatButtonModule,
    MatIconModule
  ],
  standalone: true
})

export class GeolocationComponent implements OnInit, AfterViewInit, OnDestroy {

  private subscriptions: Subscription;
  private geolocStateUpdate: WritableSignal<GeolocStateUpdate> = signal(GeolocStateUpdate.INIT);
  public updatingGeolocState: Signal<boolean> = computed(() => { return this.geolocStateUpdate() !== GeolocStateUpdate.DONE });
  //private lastPositionTimestamp: number | undefined;
  private accuracyInMeters: number = 0.0;
  //degrees to radians is 45 * Math.PI / 180;
  //when accuracy is low, then the heading will not be available in the returned location object
  private headingInDegrees: number | undefined;
  //in m/s , to calculate km/h multiply by * 3.6
  //when accuracy is low, then the speed will not be available in the returned location object
  private speedInMetersPerSecond: number | undefined;
  //average walking speed is 3km/h to 5-6km/h
  //2km/h seems the best speed to avoid inacccurate headings or "ghost" headings/movements when accuracy is lower
  //when the device stops moving, geolocation api speed value 0, the api normally also stops providing a value for the heading
  //in m/s
  private readonly minimumSpeedForShowingHeading: number = 2.0 / 3.6;
  //GPS-enabled smartphones are typically accurate to within a 5m radius
  //accuracyInMeters should be <= minimumAccuracyForShowingHeading before showing the heading
  //the smaller the number, the better the accuracy
  //at <=5.0 it usually becomes much more reliable, but then the arrow disappears too often when the gps connection is bad, so we put it higher
  //expected GPS accuracy in dense cities typically drops to 5 to 15 meters, compared to 2 to 4 meters in open areas.
  private readonly minimumAccuracyForShowingHeading: number = 15.0; //10.0
  //from a certain speed we will not take the heading from the gps position object anymore, which is compass based on ios/android
  //we will calculate the course from the last three coordinates in this.lastPositions, because on e.g. a bike, the compass heading is not always stable
  //in m/s
  private readonly minimumSpeedForCourseHeadingOnly: number = 10.0 / 3.6;
  private lastPositions: Coordinate[] = [];
  //if the state was focused before (e.g. before page refresh etc.), we will try to continue with this state
  //one of the parameters to decide if we continue with the focused state, is if the new posistion is in x meters from the map center 
  private readonly maximumRefocusDistanceInMeters: number = 100;
  //when panning or mousescroll, focus is only lost if the difference is >= this value
  //when not manually panning/mousescroll and value of accuracyInMeters is more than this number, then we use the value of accuracyInMeters
  private readonly panningDistanceInMetersToLoseFocus: number = 20;
  //in milliseconds, startup setting for the geolocation api
  //if the app was minimized and the last recieved position was longer than x milliseconds, then we restart the position listener 
  private readonly updateIntervalInMs = 4000;
  //in milliseconds, startup setting for the geolocation api
  //should be less than updateIntervalInMs
  private readonly minUpdateIntervalInMs = 2000;
  //in milliseconds, startup setting for the geolocation api
  private readonly maxUpdateWaitBeforeTimeoutInMs = 15000;

  //private geolocation: Geolocation;
  private geolocationId: string | undefined;
  private geolocationLayer: VectorLayer<VectorSource>;
  public state: GeolocState = GeolocState.OFF;
  private lastRecievedPosition: Coordinate | undefined;
  private lastRecievedPositionTimestamp: number = 0;
  private lastUpdatedPositionTimestamp: number = 0;
  public zoom: number;
  public error: boolean;
  private errorMsg: string;
  private wasActiveBeforeBlurEvent: boolean;
  private wasFocusedBeforeBlurEvent: boolean;
  private unFocusPositionEventsKeys: EventsKey[] | undefined;
  // styles var
  // public styleClass: string = '';
  readonly styleClass = signal<string>('');

  private startupAction: { action: GeolocState, timestamp: number } | undefined;

  private inPdfMode: boolean;
  private centeringAfterOtherZoomToEvent: boolean;
  private centeringOnNewLocation: boolean;

  constructor(
    private _snackBar: MatSnackBar,
    private translateService: TranslateService,
    private mapService: MapService,
    private viewService: ViewService,
    private toggleEventService: ToggleEventService,
    private browserSnifferServiceerSniffer: BrowserSnifferService,
    public geolocationService: GeolocationService,
    public olLayerService: OlLayerService
  ) { }

  ngOnInit() {

    this.error = false;
    //this.styleClass = '';

    this.subscriptions = new Subscription();

    this.subscriptions.add(this.toggleEventService.emitter$.subscribe(
      (toggleEvent: ToggleEventType) => {
        switch (toggleEvent.forwardedFor) {
          case 'pdf-print': {
            if (toggleEvent.eventName === ToggleEventNameType.OPEN) {
              console.log("not updating geolocation layer on main map during pdf print process (if the gsp position changes, the main map rerenders, which makes a route disappear on the pdf map and reappear on the main map)");
              //this.inPdfMode = this.state !== GeolocState.OFF;
              this.inPdfMode = true;
            } else if (toggleEvent.eventName === ToggleEventNameType.CLOSE) {
              console.log("updating geolocation layer again on main map after pdf print process");
              this.inPdfMode = false;
              /*
              during the pause we always updated this.lastRecievedPosition & this.accuracyInMeters, but not the feature on the map
              the feature will be updated with the next event
              */
            }
            break;
          }
          case 'trace-histo-import':
          case 'gpx-import': {
            //a trace-histo-import/gpx-import + ToggleEventNameType.FILE_IMPORT is normally followed by a trace-zoomto + ToggleEventNameType.INACTIVE
            //gpx import can be from an extrenal http link,  so this componenent is possibly still starting up
            this.centeringAfterOtherZoomToEvent = (this.state !== GeolocState.OFF || this.geolocStateUpdate() !== GeolocStateUpdate.DONE) && toggleEvent.eventName === ToggleEventNameType.FILE_IMPORT;
            break;
          }
          case 'trace-zoomto':
          case 'zoom-zoomall':
          case 'geocoding-featurezoom': {
            //a trace-zoomto/zoom-zoomall/geocoding-featurezoom + ToggleEventNameType.ACTIVE is normally followed by a trace-zoomto/zoom-zoomall/geocoding-featurezoom + ToggleEventNameType.INACTIVE
            this.centeringAfterOtherZoomToEvent = (this.state !== GeolocState.OFF || this.geolocStateUpdate() !== GeolocStateUpdate.DONE) && toggleEvent.eventName === ToggleEventNameType.ACTIVE;
            break;
          }
          case 'MapService': {
            if (this.state !== GeolocState.OFF) {
              if (toggleEvent.eventName === ToggleEventNameType.VISIBLE) {
                this.optionalLocationUpdateAfterWindowBlur();
              } else if (toggleEvent.eventName === ToggleEventNameType.INVISIBLE) {
                this.optionalWindowBlurRegistration();
              }
            }
          }
        }
      }
    ));

    this.subscriptions.add(this.mapService.getMapReady$().subscribe((isReady: boolean) => {

      if (!isReady || this.geolocStateUpdate() !== GeolocStateUpdate.INIT || this.state !== GeolocState.OFF) {
        return;
      }

      this.geolocStateUpdate.set(GeolocStateUpdate.AUTO_DOING);

      this.saveSetting('Geolocation.grantedcheck', false);

      let isFirstAutoStartupOnMObile = false;
      if (this.browserSnifferServiceerSniffer.isMobileOS) {
        isFirstAutoStartupOnMObile = this.getSavedSetting('Geolocation.firstautostartup') === null;
        if (isFirstAutoStartupOnMObile) {
          this.saveSetting('Geolocation.firstautostartup', true);
        }
      }

      //little timeout
      //  - for this.centeringOnRouteOrRouteImport to be set at startup when clicking on a stored local gpx file
      //  - for smoother route reload on page refresh
      //if this.centeringOnRouteOrRouteImport===true is not catched here, we will look for it again on position update
      window.setTimeout(() => {

        this.getGeolocationPermissionState(false).then((result: PermissionState) => {
          //for desktop we don't do auto startup, unless already granted
          if ('granted' === result || (this.browserSnifferServiceerSniffer.isMobileOS && 'denied' !== result)) {
            if (this.mapService.wasStartUpWithPositionOrZoomRelatedParams || this.centeringAfterOtherZoomToEvent) {
              //normally automatically reset to false
              //this.centeringAfterOtherZoomToEvent = false;
              this.offToUnFocused();
            } else {
              this.offToFocused();
            }
          } else {
            this.geolocStateUpdate.set(GeolocStateUpdate.DONE);

            console.log("Geolocation.checkPermissions: we didn't automatically start up geolocalisation, current state is " + result);

            //class 'permission_denied_before' & class 'permission_denied_now' both show the same icon, but class 'permission_denied_now' has a red color
            //if not asked before, we show the normal 'off' icon
            this.styleClass.set('denied' === result ? 'permission_denied_before' : '');

            //on mobile os we show a message when permission is 'denied' and it's the first time we try to start up geolocalisation
            if ('denied' === result && isFirstAutoStartupOnMObile) {
              this.translateService.get('geolocation.error.permission_denied').subscribe((translation: string) => {
                this.errorMsg = translation;
              });
              this.showErrorMsg();
            }
          }
        }).catch((error: any) => {
          this.geolocStateUpdate.set(GeolocStateUpdate.DONE);

          //show error icon(?)
          this.styleClass.set('error_during_startup');

          this.translateService.get('geolocation.error.startup_error').subscribe((translation: string) => {
            this.errorMsg = translation + (error && error.message ? ' (' + error.message + ')' : '');
          });
          this.showErrorMsg();

          console.error('Geolocation.checkPermissions: unable to check permissions, not starting up geolocalisation', error);
        })

      }, 2000);

    }));

    /* 
    //using togglevent now
    window.addEventListener('blur', () => this.optionalWindowBlurRegistration());
    window.addEventListener('focus', () => this.optionalLocationUpdateAfterWindowBlur());
    */
  }

  ngAfterViewInit() { }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();

    /* 
    //using togglevent now
    window.removeEventListener('blur', () => this.optionalWindowBlurRegistration());
    window.removeEventListener('focus', () => this.optionalLocationUpdateAfterWindowBlur());
    }
    */

    //this also clears the unfocus listener
    this.clearWatch(true);

    if (this.geolocationLayer) {
      //this.mapService.getMap().removeLayer(this.geolocationLayer);
      this.olLayerService.removeOlLayerFromMap(this.geolocationLayer)
    }
  }

  private getSavedSetting(settingsKey: string): string | null {
    let result: string | null = null;

    if (localStorage) {
      switch (settingsKey) {
        case 'Geolocation.firstautostartup':
        case 'Geolocation.grantedcheck': {
          result = localStorage.getItem(settingsKey);
          //check if it's too old?
          /*
          if (result) {
            result = isNaN(parseInt(result)) || parseInt(result) < (Date.now() - (24 * 60 * 60 * 1000)) ? null : result
            if (!result) {
              localStorage.removeItem(settingsKey);
            }
          }
          */
          break;
        }
        default: {
          console.error('setting cannot be fetched, unknown key : ' + settingsKey);
        }
      }
    }

    return result;
  }

  /**
   * 
   * @param settingsKey 
   * @param add  true for adding (we'll add Date.now().toString() as value), false for removing
   */
  private saveSetting(settingsKey: string, add: boolean) {
    if (localStorage) {
      switch (settingsKey) {
        case 'Geolocation.firstautostartup':
        case 'Geolocation.grantedcheck': {
          if (add) {
            localStorage.setItem(settingsKey, Date.now().toString());
          } else {
            localStorage.removeItem(settingsKey);
          }
          break;
        }
        default: {
          console.error('setting cannot be saved, unknown key : ' + settingsKey);
        }
      }
    }
  }

  private async getGeolocationPermissionState(askPermissionOnNativePlatform: boolean): Promise<PermissionState> {

    //'prompt' | 'prompt-with-rationale' | 'granted' | 'denied'
    const status: PermissionStatus = await CapacitorGeolocation.checkPermissions();

    if (status && status.location === 'granted') {
      return status.location;
    }

    //if it's already denied, don't ask again
    //CapacitorGeolocation.requestPermissions is not available on the web!
    if (status
      && status.location
      && status.location.indexOf('prompt') === 0
      && askPermissionOnNativePlatform
      && Capacitor.isNativePlatform()) {


      if (Capacitor.getPlatform() === 'ios' && this.getSavedSetting('Geolocation.grantedcheck')) {
        //granted before, but probably "allow once"
        //calling 'await CapacitorGeolocation.requestPermissions' again on ios after a "allow once" will create a deadlock , because no popup is shown
        return 'denied';
      }

      //'prompt' | 'prompt-with-rationale' | 'granted' | 'denied'
      const requestResult: PermissionStatus = await CapacitorGeolocation.requestPermissions();

      if (requestResult && requestResult.location) {
        this.saveSetting('Geolocation.grantedcheck', requestResult.location === 'granted');
        return requestResult.location
      }
    }

    if (status && status.location) {
      return status.location;
    }

    return Promise.reject(new Error('unable to check permissions via method CapacitorGeolocation.checkPermissions or get permissions from method CapacitorGeolocation.requestPermissions'));
  }

  /*
  always creates timeout?
  private async getCurrentPosition(): Promise<Coordinate | undefined> {
    try {
      const position: Position | undefined = await CapacitorGeolocation.getCurrentPosition({
        enableHighAccuracy: true,
        maximumAge: 0,
        timeout: 100
      });

      return position ? olProj.transform([position.coords.longitude, position.coords.latitude], 'EPSG:4326', 'EPSG:3812') : undefined;
    } catch (err) {
      console.error('Error requesting current position', err);
      return undefined;
    }
  }
  */

  /**
   * 
   * @param alsoClearAdditionalInfo if true, then we also reset lastRecievedPosition , lastRecievedPositionTimestamp , speedInMetersPerSecond , headingInDegrees , accuracyInMeters
   */
  private clearWatch(alsoClearAdditionalInfo: boolean) {
    this.startupAction = undefined;

    unByKey(this.unFocusPositionEventsKeys);
    delete this.unFocusPositionEventsKeys;

    //normally both automatically reset to false
    this.centeringAfterOtherZoomToEvent = false;
    this.centeringOnNewLocation = false;

    if (alsoClearAdditionalInfo) {
      this.lastRecievedPosition = undefined;
      this.lastRecievedPositionTimestamp = 0;
      this.lastUpdatedPositionTimestamp = 0;
      this.speedInMetersPerSecond = undefined;
      this.headingInDegrees = undefined;
      this.accuracyInMeters = 0.0;
      this.lastPositions = [];
    }

    if (this.geolocationId) {
      const oldId = this.geolocationId
      this.geolocationId = undefined;
      CapacitorGeolocation.clearWatch({ id: oldId });
    }
  }

  private async startGeolocationTracking(startupActionParam: { action: GeolocState, timestamp: number }) {

    this.error = false;
    this.styleClass.set('');

    //already gave permission? if not, in native apps we ask directly via this method
    //the browser will show the allow/deny menu itself when starting up geolocation
    let permissionState: PermissionState = null;
    try {
      permissionState = await this.getGeolocationPermissionState(true);
      console.log("startGeolocationTracking > getGeolocationPermissionState > " + permissionState);
    } catch (error) {
      console.error("startGeolocationTracking, error during call to method getGeolocationPermissionState", error);
    }

    if (!permissionState || 'denied' === permissionState) {
      this.error = true;
      //class 'permission_denied_before' & class 'permission_denied_now' both show the same icon, but class 'permission_denied_now' has a red color
      this.styleClass.set('permission_denied_now');
      this.state = GeolocState.OFF;

      //normally not needed, unable to start up
      //this.geolocationService.updatedPosition({geolocState: GeolocState.OFF});

      this.geolocStateUpdate.set(GeolocStateUpdate.DONE);

      this.translateService.get('geolocation.error.permission_denied').subscribe((translation: string) => {
        this.errorMsg = translation;
      });
      this.showErrorMsg();

      return;
    }

    if (!this.geolocationLayer) {
      const accuracyFeature = new Feature({ 'locationFeatureType': 'accuracyFeature' });
      accuracyFeature.setId('accuracyFeature');
      const positionFeature = new Feature({ 'locationFeatureType': 'positionFeature' });
      positionFeature.setId('positionFeature');
      const headingFeature = new Feature({ 'locationFeatureType': 'headingFeature' });
      headingFeature.setId('headingFeature');

      //not yet visible
      this.geolocationLayer = new VectorLayer({
        className: 'geolocationLayer',
        //now done via addOlLayerToMap
        //map: this.mapService.getMap(),
        source: new VectorSource({
          features: [accuracyFeature, positionFeature, headingFeature]
        }),
        visible: false,
        zIndex: 363,
        style: (feature: Feature, resolution: number) => { return this.getLocationLayerStyle(feature, resolution) }
      });
      this.geolocationLayer.set('customId', 'geolocationLayer');

      this.olLayerService.addOlLayerToMap(this.geolocationLayer);
    }

    if (!this.geolocationId) {

      let startupActionLocal = startupActionParam;
      this.startupAction = startupActionParam;

      try {
        this.geolocationId = await CapacitorGeolocation.watchPosition(
          {
            //High accuracy mode
            //default false
            enableHighAccuracy: true,
            //The maximum age in milliseconds of a possible cached position that is acceptable to return
            //default 0
            maximumAge: 0,
            //The maximum wait time in milliseconds for location updates.
            //default 10000
            timeout: this.maxUpdateWaitBeforeTimeoutInMs,
            //Desired interval in milliseconds to receive location updates in watchPosition.
            //default is value from timeout
            interval: this.updateIntervalInMs,
            //The minimum update interval for watchPosition.
            //default 5000 , android only
            minimumUpdateInterval: this.minUpdateIntervalInMs < this.updateIntervalInMs ? this.minUpdateIntervalInMs : this.updateIntervalInMs + 100,
            //Whether to fall back to the Android framework's LocationManager in case Google Play Service's location settings checks fail.
            //android only, default true
            enableLocationFallback: true
          },
          (position: Position | null, error?: any) => {

            if (this.inPdfMode) {
              //we should not trigger a map render during print mode

              if (!error && position) {
                this.accuracyInMeters = position.coords.accuracy;
                this.lastRecievedPositionTimestamp = Date.now();
                this.lastRecievedPosition = olProj.transform([position.coords.longitude, position.coords.latitude], 'EPSG:4326', 'EPSG:3812');
              }

              if (error || !position) {
                this.clearWatch(true);
                this.error = true;
                this.styleClass.set('error_after_startup');
                this.state = GeolocState.OFF;
                this.geolocationService.updatedPosition({ geolocState: GeolocState.OFF });
                this.geolocationLayer.setVisible(false);
                //no map render!
                //this.geolocationLayer.getSource().changed();
                //no error message(?)
              }

              //set geolocStateUpdate to DONE to be sure it's been reset
              this.geolocStateUpdate.set(GeolocStateUpdate.DONE);

              return;
            }

            if (error || !position) {

              console.error("CapacitorGeolocation.watchPosition: error after startup", error);

              //this also clears the unfocus listener
              this.clearWatch(true);

              this.error = true;
              //class gets updated below
              this.styleClass.set('');
              this.state = GeolocState.OFF;

              this.geolocationService.updatedPosition({ geolocState: GeolocState.OFF });

              this.geolocationLayer.setVisible(false);

              //sometimes the setVisible(false) is not direclty done, only after you pan the map, the dot disappears
              this.geolocationLayer.getSource().changed();

              //this.geolocStateUpdate() will be set to DONE after this if/else
              if (this.geolocStateUpdate() === GeolocStateUpdate.AUTO_DOING) {
                //we are trying to do an automatic geolocate at startup

                this.styleClass.set('error_during_startup');

                this.translateService.get('geolocation.error.startup_error').subscribe((translation: string) => {
                  this.errorMsg = translation + (error && error.message ? ' (' + error.message + ')' : '');
                });

                this.showErrorMsg();

              } else {

                this.errorMsg = undefined;
                let codeFound = false;

                if (Capacitor.isNativePlatform()) {
                  switch (error && typeof error.code === 'string' ? error.code : '') {
                    case 'OS-PLUG-GLOC-0003':
                    case 'OS-PLUG-GLOC-0008':
                    case 'OS-PLUG-GLOC-0009': {
                      codeFound = true;
                      this.styleClass.set('permission_denied_now');

                      this.translateService.get('geolocation.error.permission_denied').subscribe((translation: string) => {
                        this.errorMsg = translation;
                      });

                      break;
                    }
                    case 'OS-PLUG-GLOC-0010': {
                      codeFound = true;

                      this.translateService.get('geolocation.error.timeout').subscribe((translation: string) => {
                        this.errorMsg = translation;
                      });
                      break;
                    }
                    case 'OS-PLUG-GLOC-0002': {
                      codeFound = true;

                      this.translateService.get('geolocation.error.position_unavailable').subscribe((translation: string) => {
                        this.errorMsg = translation;
                      });
                      break;
                    }
                    case 'OS-PLUG-GLOC-0007': {
                      codeFound = true;
                      this.styleClass.set('permission_denied_now');

                      this.translateService.get('geolocation.error.gps_deactivated').subscribe((translation: string) => {
                        this.errorMsg = translation;
                      });

                      break;
                    }
                    case 'OS-PLUG-GLOC-0004':
                    case 'OS-PLUG-GLOC-0005':
                    case 'OS-PLUG-GLOC-0006':
                    case 'OS-PLUG-GLOC-0011':
                    case 'OS-PLUG-GLOC-0012':
                    case 'OS-PLUG-GLOC-0013':
                    case 'OS-PLUG-GLOC-0014':
                    case 'OS-PLUG-GLOC-0015':
                    case 'OS-PLUG-GLOC-0016': {
                      codeFound = true;
                      this.errorMsg = "General settings error (error " + error.code + ")";
                      break;
                    }
                  }
                } else {
                  switch (error && typeof error.code === 'number' ? error.code : -1) {
                    case 1: {
                      codeFound = true;
                      this.styleClass.set('permission_denied_now');

                      this.translateService.get('geolocation.error.permission_denied').subscribe((translation: string) => {
                        this.errorMsg = translation;
                      });

                      break;
                    }
                    case 3: {
                      codeFound = true;
                      this.translateService.get('geolocation.error.timeout').subscribe((translation: string) => {
                        this.errorMsg = translation;
                      });
                      break;
                    }
                    case 2: {
                      codeFound = true;
                      this.translateService.get('geolocation.error.position_unavailable').subscribe((translation: string) => {
                        this.errorMsg = translation;
                      });
                      break;
                    }
                  }
                }

                if (!this.styleClass) {
                  this.styleClass.set('error_after_startup');
                }

                if (!codeFound) {
                  //default to "position is not available"
                  this.translateService.get('geolocation.error.position_unavailable').subscribe((translation: string) => {
                    this.errorMsg = translation + (error && error.message ? ' (' + error.message + ')' : '');
                  });
                }

                this.showErrorMsg();
              }

              //error can happen at startup, so set geolocStateUpdate to DONE here too
              this.geolocStateUpdate.set(GeolocStateUpdate.DONE);

              return;
            }

            /*
            //we already stopped above at test "if (error || !position)"
            if (!position) {
              //set geolocStateUpdate to DONE to be sure it's been reset
              this.geolocStateUpdate.set(GeolocStateUpdate.DONE);
              return;
            }
            */

            //was there an error before, then reset
            if (this.error) {
              this.error = false;
              this.styleClass.set('unfocused');
            }

            //position.timestamp
            this.lastRecievedPositionTimestamp = Date.now();
            const oldPosition: Coordinate | undefined = this.lastRecievedPosition;
            let newPosition: Coordinate | undefined = undefined;
            let isUpdatedPosition = false;
            try {
              newPosition = olProj.transform([position.coords.longitude, position.coords.latitude], 'EPSG:4326', 'EPSG:3812');
              isUpdatedPosition = true;

              //we keep 3 last positions
              if (this.lastPositions.length > 2) {
                this.lastPositions.shift()
              }
              //this.lastPositions.push([position.coords.longitude, position.coords.latitude]);
              this.lastPositions.push(newPosition);
            } catch (error) {
              console.error("unable to transform recieved position to EPSG:3812");
              console.error(position);
            }

            if (oldPosition && newPosition && !startupActionLocal) {
              //don't update for every little difference (but no extra test on startup/restart >startupActionLocal)
              if (new LineString([oldPosition, newPosition]).getLength() <= 1) {
                isUpdatedPosition = false;
              }

              //but if it was too long ago that we updated the features, then we accept even a small change
              // old test ... && this.lastUpdatedPositionTimestamp < (Date.now() - (this.updateIntervalInMs * 2))
              if (!isUpdatedPosition && this.lastUpdatedPositionTimestamp > 0 && this.lastUpdatedPositionTimestamp < (Date.now() - this.maxUpdateWaitBeforeTimeoutInMs)) {
                isUpdatedPosition = true;
              }
            }

            if (isUpdatedPosition) {
              this.lastRecievedPosition = newPosition;
              this.lastUpdatedPositionTimestamp = this.lastRecievedPositionTimestamp;
            }

            const oldHeadingInDegrees = this.headingInDegrees;
            this.headingInDegrees = typeof position.coords.heading === 'number' ? position.coords.heading : undefined;

            const oldSpeedInMetersPerSecond = this.speedInMetersPerSecond;
            this.speedInMetersPerSecond = typeof position.coords.speed === 'number' ? position.coords.speed : undefined;

            const oldAccuracyInMeters = this.accuracyInMeters;
            //don't update for every little difference
            if (isUpdatedPosition || (Math.abs(oldAccuracyInMeters - position.coords.accuracy) > 1)) {
              this.accuracyInMeters = position.coords.accuracy;
            }

            if (this.accuracyInMeters <= this.minimumAccuracyForShowingHeading
              && this.speedInMetersPerSecond >= this.minimumSpeedForCourseHeadingOnly
              && this.lastPositions.length >= 3) {
              //correct heading programmatically to avoid an unstable heading indicator when going faster like on a bike
              //we use 3 last positions (two positions would work but three gives a more nuanced result)
              const averageHeading = this._calculateAverageBearing();
              //method _calculateAverageBearing checks for negative values
              if (averageHeading !== null /*&& averageHeading >= 0*/) {
                this.headingInDegrees = averageHeading;
              }
            }

            let shouldUpdateLayerForHeadingOrSpeedIndicator = false;

            if (isUpdatedPosition) {
              //new position (with at least more than x centimeters difference compared to old position)
              //we should only arrive here if newPosition is available, check for newPosition is not really necesasry
              this.geolocationLayer.getSource().getFeatureById('accuracyFeature').setGeometry(newPosition ? new Circle(newPosition, this.accuracyInMeters) : null);
              this.geolocationLayer.getSource().getFeatureById('positionFeature').setGeometry(newPosition ? new Point(newPosition) : null);
              this.geolocationLayer.getSource().getFeatureById('headingFeature').setGeometry(newPosition ? new Point(newPosition) : null);
            } else if (oldAccuracyInMeters !== this.accuracyInMeters) {
              //same position, but updated accuracy circle
              const oldGeometryWithUpdatedRadius = this.geolocationLayer.getSource().getFeatureById('accuracyFeature').getGeometry() as Circle;
              oldGeometryWithUpdatedRadius.setRadius(this.accuracyInMeters);
              this.geolocationLayer.getSource().getFeatureById('accuracyFeature').setGeometry(oldGeometryWithUpdatedRadius);
            } else {
              //trigger a geolocationLayer source update (because no geometry was updated above) in case of :
              //heading styling needs to be updated
              //heading angle needs to be updated

              //accuracy in meters, the smaller the better the accuracy
              shouldUpdateLayerForHeadingOrSpeedIndicator = (oldAccuracyInMeters > this.minimumAccuracyForShowingHeading && this.accuracyInMeters <= this.minimumAccuracyForShowingHeading)
                || (oldAccuracyInMeters <= this.minimumAccuracyForShowingHeading && this.accuracyInMeters > this.minimumAccuracyForShowingHeading);

              //speed can de undefined  
              if (!shouldUpdateLayerForHeadingOrSpeedIndicator) {
                if (typeof oldSpeedInMetersPerSecond === 'number' && typeof this.speedInMetersPerSecond === 'number') {
                  shouldUpdateLayerForHeadingOrSpeedIndicator = (oldSpeedInMetersPerSecond < this.minimumSpeedForShowingHeading && this.speedInMetersPerSecond >= this.minimumSpeedForShowingHeading)
                    || (oldSpeedInMetersPerSecond >= this.minimumSpeedForShowingHeading && this.speedInMetersPerSecond < this.minimumSpeedForShowingHeading);
                } else if (typeof oldSpeedInMetersPerSecond === 'number') {
                  //new speed is undefined, and old speed was high enough for marker?
                  shouldUpdateLayerForHeadingOrSpeedIndicator = oldSpeedInMetersPerSecond >= this.minimumSpeedForShowingHeading;
                } else if (typeof this.speedInMetersPerSecond === 'number') {
                  //old speed was undefined, and new speed is high enough for marker?
                  shouldUpdateLayerForHeadingOrSpeedIndicator = this.speedInMetersPerSecond >= this.minimumSpeedForShowingHeading;
                }
              }

              //heading can be undefined
              shouldUpdateLayerForHeadingOrSpeedIndicator = shouldUpdateLayerForHeadingOrSpeedIndicator
                || this.headingInDegrees !== oldHeadingInDegrees;
            }

            //startupActionLocal? and still the same values as a few milliseconds ago?
            let alreadyCenteredIfNecessary = false;
            if (startupActionLocal
              && this.startupAction
              && startupActionLocal.timestamp === this.startupAction.timestamp) {

              //this.finalizeOffToOn will set GeolocStateUpdate.DONE
              this.finalizeOffToOn(!this.centeringAfterOtherZoomToEvent && this.startupAction.action === GeolocState.FOCUSED);
              //finalizeOffToOn will call method locate and this will do a zoom/center if necessary
              alreadyCenteredIfNecessary = true;
              this.startupAction = undefined;
            }

            if (this.geolocStateUpdate() !== GeolocStateUpdate.DONE) {
              //should have status DONE
              return;
            }

            //force layer update
            if (isUpdatedPosition || oldAccuracyInMeters !== this.accuracyInMeters || shouldUpdateLayerForHeadingOrSpeedIndicator) {
              this.geolocationLayer.getSource().changed();
            }

            if (isUpdatedPosition) {
              this.geolocationService.updatedPosition({ geolocState: this.state, position: newPosition });
            }

            if (this.state === GeolocState.FOCUSED) {
              if (this.centeringAfterOtherZoomToEvent) {
                //extra try to unfocus
                this.unfocusEventHandler(null);
                //normally automatically reset to false
                //this.centeringAfterOtherZoomToEvent = false;
              } else if (isUpdatedPosition && !alreadyCenteredIfNecessary) {
                //there was no center action in the locate method (via finalizeOffToOn)
                this.centerMapOnPosition();
              }
            }

          });
      } catch (err) {
        console.error('Error while starting up geolocation watchPosition listener', err);

        //normally not necessary, because we're still at the startup
        this.clearWatch(true);

        this.error = true;
        this.styleClass.set('error_during_startup');
        this.state = GeolocState.OFF;
        //normally not yet visible at startup
        this.geolocationLayer.setVisible(false);

        this.geolocationService.updatedPosition({ geolocState: GeolocState.OFF });

        this.geolocStateUpdate.set(GeolocStateUpdate.DONE);

        this.translateService.get('geolocation.error.startup_error').subscribe((translation: string) => {
          this.errorMsg = translation + (err && err.message ? ' (' + err.message + ')' : '');
        });

        this.showErrorMsg();

        console.error("CapacitorGeolocation.watchPosition: error during startup", err);
      }
    }
  }

  /**
   * Calculates the average bearing in radians from an array of coordinates.
   * @returns {number|null} Average angle in radians (-PI to PI), or null if insufficient data.
   */
  private _calculateAverageBearing() {
    if (this.lastPositions.length < 2) {
      return null;
    }

    let sumSin = 0;
    let sumCos = 0;
    let count = 0;

    for (let i = 0; i < this.lastPositions.length - 1; i++) {
      const p1 = this.lastPositions[i];
      const p2 = this.lastPositions[i + 1];

      const dx = p2[0] - p1[0]; // Difference in X (Longitude)
      const dy = p2[1] - p1[1]; // Difference in Y (Latitude)

      // Skip identical consecutive points to avoid 0/0 errors
      if (dx === 0 && dy === 0) {
        continue;
      }

      // Standard Cartesian angle (0 = East, counter-clockwise)
      const cartesianAngle = Math.atan2(dy, dx);
      // Convert to Map Navigation angle (0 = North, clockwise)
      // Formula: Navigation Angle = (PI / 2) - Cartesian Angle
      const mapAngle = (Math.PI / 2) - cartesianAngle;

      sumSin += Math.sin(mapAngle);
      sumCos += Math.cos(mapAngle);
      count++;
    }

    if (count === 0) {
      return null;
    }

    // average heading
    const avgRadians = Math.atan2(sumSin / count, sumCos / count);

    // Transform radians into degrees
    let degrees = avgRadians * (180 / Math.PI);

    // Clean up negative angles (e.g., -90° West becomes 270°)
    if (degrees < 0) {
      degrees += 360;
    }

    return degrees;
  }

  private getLocationLayerStyle(feature: Feature, resolution: number): Style[] {

    const zoomLevel = this.mapService.getCurrentMap().getView().getZoomForResolution(resolution);
    const positionFeatureRadius = zoomLevel < 5 ? 4 :
      zoomLevel < 8 ? 4 + zoomLevel / 4 :
        zoomLevel < 9 ? 6 :
          4 + zoomLevel / 2;
    const positionFeatureStrokeWidth = zoomLevel < 5 ? 2 :
      zoomLevel < 8 ? 2 :
        zoomLevel < 9 ? 3 :
          3 + zoomLevel / 12;
    // console.log('positionFeatureRadius positionFeatureStrokeWidth', positionFeatureRadius, positionFeatureStrokeWidth);

    let style: Style[] = [];

    switch (feature.getProperties()['locationFeatureType']) {
      case 'headingFeature': {

        //in case this.headingInDegrees is undefined, we will return no style, the arrow will disappear
        //only from a certain zoom levels the arrow will appear, otherwise no style is returned
        //only from a certain speed the arrow will appear, otherwise no style is returned
        if (typeof this.headingInDegrees === 'number'
          && typeof this.speedInMetersPerSecond === 'number'
          && this.viewService.getZoom() >= 8
          && this.speedInMetersPerSecond >= this.minimumSpeedForShowingHeading
          && this.accuracyInMeters <= this.minimumAccuracyForShowingHeading) {

          const headingIndicator = new RegularShape({
            points: 3,
            radius: positionFeatureRadius - 1,
            fill: new Fill({
              color: [12, 190, 224, 1.0],
            }),
            stroke: new Stroke({
              lineCap: 'round',
              lineJoin: 'round',
              width: 3,
              color: [12, 190, 224, 1.0],//'white'
            }),
            rotateWithView: true,
          });
          //const scale = this.speedInMetersPerSecond / 10;
          //head.setDisplacement([0, headingIndicator.getRadius() / 2 * scale]);
          headingIndicator.setDisplacement([0, headingIndicator.getRadius() / 2 + positionFeatureRadius - 2]);//+ 5 + 5
          headingIndicator.setRotation(this.headingInDegrees * Math.PI / 180);

          style.push(
            new Style({ image: headingIndicator, zIndex: 1 })
          );
        }
        break;
      }
      case 'positionFeature': {
        style.push(
          new Style({
            zIndex: 2,
            image: new CircleStyle({
              radius: positionFeatureRadius,
              fill: new Fill({
                color: [12, 190, 224, 1.0]
              }),
              stroke: new Stroke({
                color: [255, 255, 255, 1],
                width: positionFeatureStrokeWidth
              })
            })
          })
        );
        break;
      }
      case 'accuracyFeature': {
        style.push(
          new Style({
            zIndex: 0,
            fill: new Fill({
              color: [12, 190, 224, 0.1]
            }),
            stroke: new Stroke({
              color: [12, 190, 224, 0.9],
              width: 1
            }),
          })
        );
        break;
      }
    }

    return style;
  }

  onGeolocClick(evt: Event) {
    if (this.geolocStateUpdate() === GeolocStateUpdate.DONE) {
      this.geolocStateUpdate.set(GeolocStateUpdate.DOING);
      //GeolocStateUpdate.DONE is usually set in the methods finalizeOffToOn/focusedToOff

      //this.watchForZoomAndPosChange();
      switch (this.state) {
        case GeolocState.OFF: {
          this.offToFocused();
          break;
        }
        case GeolocState.FOCUSED: {
          this.focusedToOff();
          break;
        }
        case GeolocState.UNFOCUSED: {
          this.unfocusedToFocused();
          break;
        }
        default: {
          this.geolocStateUpdate.set(GeolocStateUpdate.DONE);
          console.error("state not implemented yet");
        }
      }
    }
  };

  private offToFocused() {
    this.startGeolocationTracking({ action: GeolocState.FOCUSED, timestamp: Date.now() });
  }

  private offToUnFocused() {
    this.startGeolocationTracking({ action: GeolocState.UNFOCUSED, timestamp: Date.now() });
  }

  private finalizeOffToOn(isFocusEvent: boolean) {
    this.geolocationLayer.setVisible(true);
    //this.state is set in method locate;
    this.locate(isFocusEvent);

    this.geolocStateUpdate.set(GeolocStateUpdate.DONE);

    this.geolocationLayer.getSource().changed();
  }

  private focusedToOff() {

    //this also clears the unfocus listener
    this.clearWatch(true);

    this.styleClass.set('');
    this.geolocationLayer.setVisible(false);
    this.state = GeolocState.OFF;

    this.geolocationService.updatedPosition({ geolocState: GeolocState.OFF });

    this.error = false;

    this.geolocStateUpdate.set(GeolocStateUpdate.DONE);

    this.geolocationLayer.getSource().changed();
  }

  private unfocusedToFocused() {

    //manual refocus
    //--
    //normally both automatically reset to false
    this.centeringAfterOtherZoomToEvent = false;
    this.centeringOnNewLocation = false;

    //if there was an error, state will also be off
    if (this.state !== GeolocState.OFF && this.lastRecievedPosition && this.lastRecievedPositionTimestamp > 0 && this.lastRecievedPositionTimestamp >= (Date.now() - this.maxUpdateWaitBeforeTimeoutInMs)) {

      this.finalizeOffToOn(true);
      //finalizeOffToOn will also have set GeolocStateUpdate.DONE and state

    } else {
      //restart
      this.clearWatch(false);

      this.error = false;
      //looks strange if the layer disappears a few seconds
      //this.locationLayer.setVisible(false);

      this.offToFocused();

      //offToFocused will also have set GeolocStateUpdate.DONE and state
    }

  }

  private optionalWindowBlurRegistration() {
    //if there was an error, state will also be off
    if (this.state !== GeolocState.OFF) {
      this.wasActiveBeforeBlurEvent = true;
      this.wasFocusedBeforeBlurEvent = this.state === GeolocState.FOCUSED;
    }
  }

  private optionalLocationUpdateAfterWindowBlur() {

    const wasActiveBeforeBlurEvent = this.wasActiveBeforeBlurEvent;
    this.wasActiveBeforeBlurEvent = false;
    const wasFocusedBeforeBlurEvent = this.wasFocusedBeforeBlurEvent;
    this.wasFocusedBeforeBlurEvent = false;

    if (wasActiveBeforeBlurEvent && this.geolocStateUpdate() === GeolocStateUpdate.DONE) {

      this.geolocStateUpdate.set(GeolocStateUpdate.DOING);

      //if there was an error, state will also be off
      if (this.state !== GeolocState.OFF && this.lastRecievedPositionTimestamp > 0 && this.lastRecievedPositionTimestamp >= (Date.now() - this.maxUpdateWaitBeforeTimeoutInMs)) {

        this.finalizeOffToOn(wasFocusedBeforeBlurEvent);
        //finalizeOffToOn will also have set GeolocStateUpdate.DONE and state

      } else {
        //reset
        this.clearWatch(false);

        this.error = false;
        //looks strange if the layer disappears a few seconds
        //this.locationLayer.setVisible(false);

        if (wasFocusedBeforeBlurEvent) {
          this.offToFocused();
        } else {
          this.offToUnFocused();
        }

        //offToFocused/offToUnFocused will normally already have set GeolocStateUpdate.DONE and state
      }
    }
  }

  private showErrorMsg() {
    this._snackBar.open(this.errorMsg, 'ok', {
      duration: 4500,
      horizontalPosition: 'center',
      verticalPosition: 'top'
    });
  }

  private locate(isFocusEvent: boolean) {
    const oldGeolocState = this.state;

    if (this.lastRecievedPosition) {

      if (isFocusEvent) {
        //user requested focus manually
        this.state = GeolocState.FOCUSED;
      } else {
        //we arrive here on: automatic startup with state OFF, or when the window gets focus again after being e.g. minimized while geolocation was activated
        //if the old state was FOCUSED and the new position is a x(?) meters from map center, then we keep state FOCUSED
        //if the old state was OFF and the new position is a x(?) meters from map center, then we reset state to FOCUSED
        //otherwise we go to state UNFOCUSED

        if (oldGeolocState !== GeolocState.UNFOCUSED
          && new Circle(
            this.viewService.getCenter(),
            this.maximumRefocusDistanceInMeters
          ).intersectsCoordinate(this.lastRecievedPosition)) {

          //keep old focused status or from off to focused
          this.state = GeolocState.FOCUSED;
        } else {
          this.state = GeolocState.UNFOCUSED
        }
      }

      if (this.state === GeolocState.FOCUSED) {
        //shorter animation when going from unfocused to focused ?
        //const animationDuration = oldGeolocState === GeolocState.UNFOCUSED ? 500 : 1000;
        const maxAnimationDuration = 1000;

        const currentAccuracy = this.accuracyInMeters;
        const extentAccuracy = [
          this.lastRecievedPosition[0] - currentAccuracy,
          this.lastRecievedPosition[1] - currentAccuracy,
          this.lastRecievedPosition[0] + currentAccuracy,
          this.lastRecievedPosition[1] + currentAccuracy
        ];
        const mapSize = this.mapService.getMap().getSize();
        const resolutionAccuracy = Math.max(
          (extentAccuracy[2] - extentAccuracy[0]) / mapSize[0],
          (extentAccuracy[3] - extentAccuracy[1]) / mapSize[1]);

        let zoomlevelForAccuracy = Math.floor(this.mapService.getMap().getView().getZoomForResolution(resolutionAccuracy));
        let maxZoom: number = -1;
        //limit zoomlevel to 17 (maxlevel of cartoweb) or something 
        if (this.mapService.getMap().getView().getProjection().getCode() === "EPSG:3857") {
          maxZoom = this.geolocationService.hasModifiedMaxAutoZoom() ? (this.geolocationService.maxAutoZoom + 7) : 17;
        } else {
          maxZoom = this.geolocationService.hasModifiedMaxAutoZoom() ? this.geolocationService.maxAutoZoom : 10;
        }
        if (zoomlevelForAccuracy > maxZoom) { zoomlevelForAccuracy = maxZoom };

        const doAccuracyZoomOnFocused = this.mapService.getMap().getView().getZoom() !== zoomlevelForAccuracy;

        //unfocusEventHandler is not yet activated here, so no need to set centeringOnNewLocation to true
        this.mapService.getMap().getView().animate(
          {
            center: this.lastRecievedPosition,
            duration: maxAnimationDuration / 2
          },
          {
            zoom: doAccuracyZoomOnFocused ? zoomlevelForAccuracy : this.mapService.getMap().getView().getZoom(),
            duration: doAccuracyZoomOnFocused ? maxAnimationDuration / 2 : 0
          },
          () => {

            /*
            //zoom animation naar accuracy circle
            if (this.mapService.getMap().getView().getZoom() !== zoomlevelForAccuracy && this.geolocStateUpdate() !== GeolocStateUpdate.AUTO_DOING) {
              this.mapService.getMap().getView().animate(
                {
                  zoom: zoomlevelForAccuracy,
                  duration: animationDuration
                }
              );
            }
            */

            //when user moves map, stop centering...
            if (this.state === GeolocState.FOCUSED && !this.unFocusPositionEventsKeys) {

              const unFocusPositionListener = (evt: BaseEvent) => {
                this.unfocusEventHandler(evt);
              };

              this.unFocusPositionEventsKeys = [
                //pointerdrag event also will happen on mobile device with gestures when minimizing the screen, without moving the map
                //pointerdrag doesn't handle a mousewheel event
                //pointerdrag doesn't handle automatic center/zoom
                this.mapService.getMap().on('pointerdrag', unFocusPositionListener),
                //mousewheel event, where you zoom in on a different location
                //for this 'change:center' event is enough (no need to listen to 'change:resolution')
                //clicking on zoomin/zoomout button shouldn't change the center
                //automatic zoom-to event is also not catched by 'pointerdrag' event
                this.mapService.getMap().getView().on('change:center', unFocusPositionListener),
              ];
            }
          }
        );
      }
    }

    this.styleClass.set(this.state === GeolocState.FOCUSED ? 'located' : this.state === GeolocState.UNFOCUSED ? 'unfocused' : '');
  }

  unfocusEventHandler(evt: BaseEvent) {

    //console.log("unfocusEventHandler : ")

    if (this.state === GeolocState.FOCUSED) {

      if (this.geolocStateUpdate() !== GeolocStateUpdate.DONE) {
        return;
      }

      let isCenteringAfterOtherZoomToEvent = this.centeringAfterOtherZoomToEvent;
      let isCenteringOnNewLocation = this.centeringOnNewLocation;

      //in some cases we do an extra call without a BaseEvent (parameter null)
      //const wasMapEvent = evt ? true : false;
      const isPointerDrag = evt && evt.type && evt.type === 'pointerdrag';
      //pointerdrag has top priority
      //centeringAfterOtherZoomToEvent have priority over centeringOnNewLocation
      isCenteringAfterOtherZoomToEvent = !isPointerDrag && isCenteringAfterOtherZoomToEvent;
      //normally automatically reset to false
      //this.centeringAfterOtherZoomToEvent = false;
      isCenteringOnNewLocation = !isPointerDrag && !isCenteringAfterOtherZoomToEvent && isCenteringOnNewLocation;

      //console.log("unfocusEventHandler : wasMapEvent " + wasMapEvent)
      //console.log("unfocusEventHandler : isPointerDrag " + isPointerDrag)
      //console.log("unfocusEventHandler : isCenteringAfterOtherZoomToEvent " + isCenteringAfterOtherZoomToEvent)
      //console.log("unfocusEventHandler : isCenteringOnNewLocation " + isCenteringOnNewLocation)

      //on automatic recenter (when no pointer interaction and no route centering), we ignore the event
      //if (wasMapEvent && !isPointerDrag && !isCenteringOnRoute && (typeof this.speedInMetersPerSecond !== 'number' || this.speedInMetersPerSecond > 2.5)) 
      if (isCenteringOnNewLocation) {
        return;
      }

      //we allow a little margin, e.g. also the swipe gesture to minimize the app can move the map a bit
      if (!new Circle(
        this.viewService.getCenter(),
        //isPointerDrag || isCenteringOnRouteOrRouteImport || this.panningDistanceInMetersToLoseFocus >= this.accuracyInMeters ? this.panningDistanceInMetersToLoseFocus : (this.accuracyInMeters < 1000 ? this.accuracyInMeters : 1000)
        this.panningDistanceInMetersToLoseFocus
      ).intersectsCoordinate(this.lastRecievedPosition)) {
        this.state = GeolocState.UNFOCUSED;
        this.styleClass.set('unfocused');

        unByKey(this.unFocusPositionEventsKeys);
        delete this.unFocusPositionEventsKeys;

        //console.log("unfocusEventHandler : not intersects, to unfocused ")
      }

      //normally both automatically reset to false
      //this.centeringAfterOtherZoomToEvent = false;
      //this.centeringOnNewLocation = false; 

    } else if (this.unFocusPositionEventsKeys) {
      //this normally doesn't happen
      unByKey(this.unFocusPositionEventsKeys);
      delete this.unFocusPositionEventsKeys;

      //normally both automatically reset to false
      //this.centeringAfterOtherZoomToEvent = false;
      //this.centeringOnNewLocation = false; 
    }
  }

  centerMapOnPosition() {
    const lastRecievedPositionLocal = this.lastRecievedPosition;

    if (lastRecievedPositionLocal
      && this.state === GeolocState.FOCUSED
      && this.geolocStateUpdate() === GeolocStateUpdate.DONE
      && !this.centeringAfterOtherZoomToEvent
      && !this.centeringOnNewLocation) {

      //center animation will trigger the unfocusEventHandler, which will check for centeringOnNewLocation
      //this animation will trigger the unfocusEventHandler multiple times because the center moves instead of a one time move
      this.centeringOnNewLocation = true;
      this.mapService.getMap().getView().animate({
        center: lastRecievedPositionLocal,
        duration: 300,
      },
        () => {
          //after the animation is finished, we still wait a few millisseconds until the last unfocus event has been processed in method centeringOnNewLocation 
          window.setTimeout(() => {
            this.centeringOnNewLocation = false;
          }, 100);
        }
      );
    }
  }

  // because ol change position events are not reliable
  // private watchForZoomAndPosChange() {
  //   if (this.geolocation.getunfocused()) {
  //     const center = this.roundCoords(this.mapService.getMap().getView().getCenter()); // because ol getZoom() retrieves sometimes rounded and sometimes not rounded values
  //     const zoom = this.mapService.getMap().getView().getZoom();
  //     if ((this.zoom === zoom) && (this.position[0] == center[0]) && (this.position[1] == center[1])) {
  //       this.state = GeolocState.FOCUSED;
  //     } else {
  //       this.state = GeolocState.UNFOCUSED;
  //     }
  //   }
  // }

  private roundCoords(coords: any[]) {
    return [
      coords[0].toFixed(2),
      coords[1].toFixed(2),
    ]
  }
}
